import { createSlice, createAction } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import ObjectID from 'bson-objectid';
import indexOf from 'lodash/indexOf';
import remove from 'lodash/remove';
import get from 'lodash/get';
import set from 'lodash/set';
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';
import includes from 'lodash/includes';
import uniq from 'lodash/uniq';
import findIndex from 'lodash/findIndex';
import find from 'lodash/find';
import store from 'store2';
import { encounterNoteSectionSchema } from '../Utils/normalizr-schema';

export const updateEncounterNoteSection = createAction('encounterNote/updateEncounterNoteSection');
export const fetchEncounterFormByIdAction = createAction('encounterNote/fetchEncounterFormByIdAction');

export const batchUpdateEncounterSectionsAction = createAction('encounterNote/batchUpdateEncounterSectionsAction');
export const fetchEncounterChartImportBySectionId = createAction('encounterNote/fetchEncounterChartImportBySectionId');
export const postEncounterChartImport = createAction('encounterNote/postEncounterChartImport');

export const uploadEncounterNoteAttachmentAction = createAction('encounterNote/uploadEncounterNoteAttachments');

function findNextQuestionIds(initialNextQuestionIds, draft, questionsToRemove) {
  if (!initialNextQuestionIds || initialNextQuestionIds.length === 0) {
    return;
  }
  questionsToRemove.push(...initialNextQuestionIds);
  const newNextQuestionIds = [];
  initialNextQuestionIds.forEach(questionId => {
    draft.questions[questionId].options.forEach(optionId => {
      const optionObject = draft.options[optionId];
      optionObject.isSelected = false;
      optionObject.value = '';
      if (optionObject.nextQuestionsId) {
        newNextQuestionIds.push(...optionObject.nextQuestionsId);
      }
    });
  });
  findNextQuestionIds(newNextQuestionIds, draft, questionsToRemove);
}

function handleNextQuestionIds(initialNextQuestionIds, draft, questionIndex, questionsToInsert) {
  const questionsToRemove = [];
  findNextQuestionIds(initialNextQuestionIds, draft, questionsToRemove);
  const newQuestionOrder = remove(draft.activeQuestionsOrder, id => !includes(questionsToRemove, id));
  draft.activeQuestionsOrder = uniq([...newQuestionOrder.slice(0, questionIndex + 1), ...questionsToInsert, ...newQuestionOrder.slice(questionIndex + 1)]);
}

const encounterNoteSlice = createSlice({
  name: 'encounterNote',
  initialState: {
    encounterTemplateSections: {},
    questions: {},
    options: {},
    encounterNoteSections: {},
    encounterNoteSectionsSequence: [],
    activeEncounterNoteSectionId: null,
    activeQuestionsOrder: [],
    encounterNote: {},
    activeEncounter: {},
    activeSection: null,
    physicianNotes: {},
    pastPhysicianNotes: {},
  },
  reducers: {
    resetEncounterNoteSlice(state, action) {
      state.encounterTemplateSections = {};
      state.questions = {};
      state.options = {};
      state.encounterNoteSections = {};
      state.encounterNoteSectionsSequence = [];
      state.activeEncounterNoteSectionId = null;
      state.activeQuestionsOrder = [];
      state.encounterNote = {};
      state.activeEncounter = {};
      state.activeSection = null;
    },
    fetchEncounterNoteSectionsSuccess(state, action) {
      const {
        entities: { questions, options, encounterNoteSections },
        result: encounterNoteSectionsSequence,
      } = normalize(action.payload.data, [encounterNoteSectionSchema]);
      state.questions = questions;
      state.options = options;
      state.encounterNoteSections = encounterNoteSections;
      state.encounterNoteSectionsSequence = encounterNoteSectionsSequence;
    },

    fetchEncounterNoteSuccess(state, action) {
      const { data } = action.payload;
      state.encounterNote = data;
    },

    fetchEncounterDataSuccess(state, action) {
      const { data } = action.payload;
      const { id: encounterDataId } = data;
      const aekey = `activeEncounter-${encounterDataId}`;
      const pnKey = `physicianNotes-${encounterDataId}`;

      const ae = store.get(aekey);
      const pn = store.get(pnKey);

      if (typeof ae === 'object' && !isEmpty(ae)) {
        state.activeEncounter = ae;
      } else {
        store(aekey, data);
        state.activeEncounter = data;
      }
      if (isEmpty(pn)) {
        store(pnKey, {});
      }

      state.activeSection = get(data, 'template.sections[0].id', null);
      if (state.activeEncounter.template.imageUrls.filter(a => a.name === 'PhysicalExam').length === 0) {
        state.activeEncounter.template.imageUrls.push({
          name: 'PhysicalExam',
          imageUrls: [],
          originalImageUrls: [],
        });
      }
    },

    onChangeActiveEncounterSectionId(state, action) {
      const {
        payload: { id },
      } = action;
      state.activeEncounterNoteSectionId = id;
      const questionIdsArr = [];
      function getPreFilledQuestionsId(questionIds) {
        if (!questionIds || questionIds.length === 0) {
          return;
        }
        questionIds.forEach(questionId => {
          questionIdsArr.push(questionId);
          state.questions[questionId].options.forEach(optionId => {
            const { isSelected, nextQuestionsId } = state.options[optionId];
            if (isSelected) {
              getPreFilledQuestionsId(nextQuestionsId);
            }
          });
        });
      }
      // Add questions from nextQuestionIds as well
      state.encounterNoteSections[id].questions
        .filter(questionId => !state.questions[questionId].isDependent)
        .forEach(questionId => {
          const { options } = state.questions[questionId];
          questionIdsArr.push(questionId);
          options.forEach(optionId => {
            const { nextQuestionsId = [], isSelected } = state.options[optionId];
            if (isSelected) {
              getPreFilledQuestionsId(nextQuestionsId);
            }
          });
        });

      state.activeQuestionsOrder = questionIdsArr;
    },

    onChangeTextFieldOption(state, action) {
      const {
        payload: { id, fieldValue },
      } = action;
      state.options[id].isSelected = true;
      state.options[id].value = fieldValue;
    },

    onChangeRadioOption(state, action) {
      const {
        payload: { id },
      } = action;
      const { questionId } = state.options[id];
      const questionObj = state.questions[questionId];
      const questionIndex = indexOf(state.activeQuestionsOrder, questionId);
      const questionsToInsert = [];
      const initialNextQuestionIds = []; // initialNextQuestionIds to remove for which we are going to find the dependent questions to remove
      questionObj.options.map(optionId => {
        const optionObj = state.options[optionId];
        if (optionId === id) {
          optionObj.isSelected = true;
          optionObj.value = optionObj.label;
          if (optionObj.nextQuestionsId) {
            questionsToInsert.push(...optionObj.nextQuestionsId);
          }
        } else {
          optionObj.isSelected = false;
          optionObj.value = '';
          if (optionObj.nextQuestionsId) {
            initialNextQuestionIds.push(...optionObj.nextQuestionsId);
          }
        }
      });
      handleNextQuestionIds(initialNextQuestionIds, state, questionIndex, questionsToInsert);
    },

    onChangeCheckboxOption(state, action) {
      const {
        payload: { id, checked, questionId },
      } = action;
      const { options } = state.questions[questionId];
      const questionIndex = indexOf(state.activeQuestionsOrder, questionId);
      const questionsToInsert = [];
      const initialNextQuestionIds = []; // initialNextQuestionIds to remove for which we are going to find the dependent questions to remove
      options.map(optionId => {
        const optionObj = state.options[optionId];
        if (optionId === id && checked) {
          optionObj.isSelected = true;
          if (!optionObj.isOtherOption) {
            optionObj.value = optionObj.label;
          }
        }
        if (optionId === id && !checked) {
          optionObj.isSelected = false;
          optionObj.value = '';
          if (optionObj.nextQuestionsId) {
            initialNextQuestionIds.push(...optionObj.nextQuestionsId);
          }
        }
        if (optionObj.isSelected && optionObj.nextQuestionsId) {
          questionsToInsert.push(...optionObj.nextQuestionsId);
        }
      });
      handleNextQuestionIds(initialNextQuestionIds, state, questionIndex, questionsToInsert);
    },

    onClickAddAnotherResponse(state, action) {
      const {
        payload: { questionId },
      } = action;
      const { options, optionCount } = state.questions[questionId];
      const tempOptions = options.slice(0, optionCount).map(optionId => ({
        ...state.options[optionId],
        isSelected: false,
        id: ObjectID.generate(),
        value: '',
        referenceTableId: null,
      }));
      const optionIdsArr = tempOptions.map(option => {
        state.options[option.id] = option;
        return option.id;
      });
      state.questions[questionId].options = [...optionIdsArr, ...options];
    },

    onChangeLookUpOption(state, action) {
      const {
        payload: { masterId = null, value, id },
      } = action;
      state.options[id].value = value;
      state.options[id].referenceTableId = masterId;
    },

    onChangeEncounterDataSection(state, action) {
      const {
        payload: { sectionId, value },
      } = action;
      const encounterDataId = state.activeEncounter.id;
      const aeKey = `activeEncounter-${encounterDataId}`;
      const index = findIndex(state.activeEncounter.template.sections, o => o.id === sectionId);
      state.activeEncounter.template.sections[index].providerValue = value;
      store.transact(aeKey, enc => {
        enc.template.sections[index].providerValue = value;
      });
    },

    uploadEncounterNoteAttachmentSuccess(state, action) {
      const {
        payload: { data },
      } = action;
      const { originalUrls, resizedUrls } = data;

      const obj = find(state.activeEncounter.template.imageUrls, ['name', 'PhysicalExam']);
      obj.imageUrls.push(...resizedUrls);
      obj.originalImageUrls.push(...originalUrls);
    },

    onChangeActiveEncounterDataSection(state, action) {
      const {
        payload: { sectionId },
      } = action;
      state.activeSection = sectionId;
    },
    fetchEncounterChartImportBySectionIdSuccess(state, action) {
      const { data, sectionId, skipPastData } = action.payload;
      const { currentData, pastData = [] } = data;
      const encounterDataId = state.activeEncounter.id;
      const pnKey = `physicianNotes-${encounterDataId}`;
      const pn = store.get(pnKey);
      const activeLCPNSection = get(pn, sectionId, {});
      if (isEmpty(activeLCPNSection) && encounterDataId) {
        set(state, `physicianNotes[${sectionId}]`, currentData);
        store.transact(pnKey, obj => {
          if (obj) {
            obj[sectionId] = currentData;
          } else {
            return { [sectionId]: currentData };
          }
        });
      } else {
        set(state, `physicianNotes[${sectionId}]`, activeLCPNSection);
      }

      if (!skipPastData) {
        set(state, `pastPhysicianNotes[${sectionId}]`, pastData);
      }
    },
    onChangePhysicianNotes(state, action) {
      const { sectionId, value } = action.payload;
      const encounterDataId = state.activeEncounter.id;
      const pnKey = `physicianNotes-${encounterDataId}`;
      set(state, `physicianNotes[${sectionId}]`, value);
      store.transact(pnKey, obj => {
        if (obj) {
          obj[sectionId] = value;
        } else {
          return { [sectionId]: value };
        }
      });
    },
    physicianNoteDiagnosisMarkDeleted(state, action) {
      const { sectionId, diagnosisId } = action.payload;
      state.physicianNotes[sectionId] = get(state, `physicianNotes[${sectionId}]`, []).filter(diag => diag.diagnosisId !== diagnosisId || diag.referenceTableId !== diagnosisId);
    },
  },
});

export const {
  onChangeActiveEncounterSectionId,
  onChangeTextFieldOption,
  onChangeRadioOption,
  onChangeCheckboxOption,
  fetchEncounterDataSuccess,
  fetchEncounterNoteSectionsSuccess,
  fetchEncounterNoteSuccess,
  onClickAddAnotherResponse,
  onChangeLookUpOption,
  onChangeEncounterDataSection,
  uploadEncounterNoteAttachmentSuccess,
  onChangeActiveEncounterDataSection,
  resetEncounterNoteSlice,
  fetchEncounterChartImportBySectionIdSuccess,
  onChangePhysicianNotes,
  physicianNoteDiagnosisMarkDeleted,
} = encounterNoteSlice.actions;

export default encounterNoteSlice.reducer;
